/**
 * Browser Speech Synthesis helpers for reading lecture text aloud.
 */

export function lectureContentToSpeechText(raw) {
  if (!raw || typeof raw !== 'string') return '';

  let text = raw;

  // Display / block math → short spoken placeholders
  text = text.replace(/\$\$[\s\S]*?\$\$/g, ' equation. ');
  text = text.replace(/\\\[([\s\S]*?)\\\]/g, ' equation. ');
  text = text.replace(/\\\(([\s\S]*?)\\\)/g, ' ');
  text = text.replace(/\$[^$\n]+\$/g, ' ');

  text = text.replace(/\[\[([^\]]+)\]\]/g, '$1. ');

  // Common markdown / lecture markers
  text = text.replace(/^#{1,6}\s+/gm, '');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/_([^_]+)_/g, '$1');
  text = text.replace(/`([^`]+)`/g, '$1');
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+[.)]\s+/gm, '');
  text = text.replace(/^\s*Step\s+\d+\s*[:.-]?\s*/gim, 'Step. ');

  // Collapse whitespace
  text = text.replace(/\r\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]{2,}/g, ' ');
  text = text.trim();

  return text;
}

/** Split long text into speakable chunks (browser engines often choke on huge utterances). */
export function splitSpeechChunks(text, maxLen = 1600) {
  const cleaned = String(text || '').trim();
  if (!cleaned) return [];
  if (cleaned.length <= maxLen) return [cleaned];

  const chunks = [];
  let remaining = cleaned;
  while (remaining.length > maxLen) {
    let cut = remaining.lastIndexOf('. ', maxLen);
    if (cut < maxLen * 0.4) cut = remaining.lastIndexOf('\n', maxLen);
    if (cut < maxLen * 0.4) cut = remaining.lastIndexOf(' ', maxLen);
    if (cut < maxLen * 0.4) cut = maxLen;
    chunks.push(remaining.slice(0, cut + 1).trim());
    remaining = remaining.slice(cut + 1).trim();
  }
  if (remaining) chunks.push(remaining);
  return chunks.filter(Boolean);
}

export function isSpeechSynthesisSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
}

export function pickPreferredVoice(voices) {
  if (!voices?.length) return null;

  const list = Array.from(voices);
  const isEnglish = (v) => /^en\b/i.test(String(v.lang || '')) || /english/i.test(String(v.name || ''));
  const nameOf = (v) => String(v.name || '');

  // Common female voice names across Windows / Chrome / Edge / macOS
  const femaleNameRe =
    /\b(zira|jenny|aria|sara|sarah|sonia|natasha|mia|emma|emily|linda|susan|hazel|catherine|samantha|victoria|karen|moira|tessa|fiona|veena|raveena|female|woman|girl)\b/i;
  const maleNameRe =
    /\b(david|mark|guy|ryan|eric|richard|george|daniel|james|thomas|alex|male|man|boy)\b/i;

  const english = list.filter(isEnglish);
  const pool = english.length ? english : list;

  const scored = pool
    .map((voice) => {
      const name = nameOf(voice);
      let score = 0;
      if (femaleNameRe.test(name)) score += 100;
      if (/female/i.test(name)) score += 40;
      if (maleNameRe.test(name)) score -= 100;
      if (/male/i.test(name) && !/female/i.test(name)) score -= 40;
      // Prefer natural / neural voices when available
      if (/neural|natural|online|premium|enhanced/i.test(name)) score += 15;
      if (/en-US/i.test(voice.lang)) score += 8;
      else if (/en-GB|en-AU|en-IN/i.test(voice.lang)) score += 5;
      // Soft preference for local voices (more reliable)
      if (voice.localService) score += 3;
      return { voice, score };
    })
    .sort((a, b) => b.score - a.score);

  const bestFemale = scored.find((s) => s.score >= 100);
  if (bestFemale) return bestFemale.voice;

  // Fallback: highest score that isn't clearly male
  const nonMale = scored.find((s) => s.score > -50);
  return nonMale?.voice || pool[0] || list[0];
}
